import React, { useEffect, useState, useCallback, useRef } from 'react';
import { fetchPaginatedActivities } from '../services/ActivityService';
import { fetchUsers } from '../services/UserService';
import ActivitiesFeed from '../components/ActivitiesFeed/ActivitiesFeed';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { useNavigate } from 'react-router-dom';
import { Card } from 'react-bootstrap';
import Loading from '../components/Common/Loading';
import ActivityCard from '../components/ActivityCard/ActivityCard';

const RecentActivitiesOverview = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [activitiesFeed, setActivitiesFeed] = useState([]);
    const [nextPageToken, setNextPageToken] = useState(undefined);
    const [hasMore, setHasMore] = useState(true);
    const activityIds = useRef(new Set());
    const initialFetchDone = useRef(false);
    const navigate = useNavigate();

    const fetchUserData = async (usernames) => {
        try {
            const response = await fetchUsers(usernames);
            return response.users.reduce((acc, user) => {
                acc[user.username] = user;
                return acc;
            }, {});
        } catch (error) {
            console.error("Failed to fetch user data", error);
            return {};
        }
    };

    const processActivities = useCallback(async (activities) => {
        const usernames = [...new Set(activities.map(activity => activity.username))];
        const userData = await fetchUserData(usernames);

        return activities.map(activity => {
            const user = userData[activity.username];

            if (!user) {
                return {
                    ...activity,
                    username: undefined,
                    userGivenName: "Unknown Competitor",
                    profilePicture: undefined,
                    isPersonalBest: false,
                    isPersonalWorst: false,
                    photos: activity.attachments ? activity.attachments.filter(att => att.type === 'photo').map(att => att.firstName) : [],
                };
            }

            return {
                ...activity,
                username: user.username,
                userGivenName: user.firstName,
                profilePicture: user.profilePicture,
                isPersonalBest: activity.points === user.maxPoints?.points,
                isPersonalWorst: activity.points === user.minPoints?.points,
                photos: activity.attachments ? activity.attachments.filter(att => att.type === 'photo').map(att => att.firstName) : [],
            };
        }).sort((a, b) => new Date(b.date) - new Date(a.date));
    }, []);

    const loadMoreActivities = useCallback(async () => {
        if (isLoading || !initialFetchDone.current || !hasMore) return;

        setIsLoading(true);

        try {
            const response = await fetchPaginatedActivities(nextPageToken, 10);
            const fetchedActivities = response.data;

            if (!Array.isArray(fetchedActivities)) {
                console.error('Expected an array, but got:', fetchedActivities);
                return;
            }

            const newActivities = fetchedActivities.filter(activity => !activityIds.current.has(activity.activityId));
            newActivities.forEach(activity => activityIds.current.add(activity.activityId));

            const allActivities = await processActivities(newActivities);

            setActivitiesFeed(prevActivities => [...prevActivities, ...allActivities]);
            setHasMore(!response.isLast);
            setNextPageToken(response.lastEvaluatedKey);
        } catch (error) {
            console.error("Failed to fetch activities", error);
        } finally {
            setIsLoading(false);
        }
    }, [isLoading, nextPageToken, hasMore, processActivities]);

    useEffect(() => {
        if (initialFetchDone.current || isLoading) return;
        initialFetchDone.current = true;

        const fetchInitialActivities = async () => {
            setIsLoading(true);
            setNextPageToken(undefined);
            setActivitiesFeed([]);
            activityIds.current.clear();
            setHasMore(true);

            try {
                const response = await fetchPaginatedActivities(nextPageToken, 10);
                const fetchedActivities = response.data;

                if (!Array.isArray(fetchedActivities)) {
                    console.error('Expected an array, but got:', fetchedActivities);
                    return;
                }

                const newActivities = fetchedActivities.filter(activity => !activityIds.current.has(activity.activityId));
                newActivities.forEach(activity => activityIds.current.add(activity.activityId));

                const allActivities = await processActivities(newActivities);

                setActivitiesFeed(allActivities);
                setHasMore(!response.isLast);
                setNextPageToken(response.lastEvaluatedKey);
            } catch (error) {
                console.error("Failed to fetch activities", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchInitialActivities();
    }, [isLoading, nextPageToken, processActivities]);

    useInfiniteScroll(loadMoreActivities);

    const handleUsernameClick = useCallback((username) => {
        navigate(`/profile/${username}`);
    }, [navigate]);

    return (
        <>
            <ActivitiesFeed activities={activitiesFeed} onUsernameClick={handleUsernameClick} />
            <div id="scroll-anchor" style={{ height: '1px' }}></div>
            {isLoading && (
                <Card.Body>
                    <Loading />
                </Card.Body>
            )}
        </>
    );
};

export default RecentActivitiesOverview;
